/**
 * TELV1-186 — Project type selection screen shown as the first step
 * when creating a new project. The user picks Interview (full Tellet
 * study) or Embed (1–3 questions, iframe-embeddable) before naming
 * the project. The selection flows into the create-project API call
 * via the `project_type` field on the DTO.
 *
 * Card style matches the existing `w-[260px] h-[260px]` pattern from
 * the original ChooseProjectCreationType so the visual weight stays
 * identical.
 */
import React from 'react';
import { MessageSquare, Code, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import useTranslation from '@/hooks/useTranslation';
import type { ProjectType } from '@/types';

interface IButtons {
  icon: React.ReactNode;
  title: string;
  description: string;
  icon_color: string;
  bg_color: string;
  type: string;
  projectType: ProjectType;
}

interface ICreationTypeProps {
  setCreationType: React.Dispatch<React.SetStateAction<string>>;
  setProjectType: React.Dispatch<React.SetStateAction<ProjectType>>;
  closeModalAndRemoveData: () => void;
}

function ChooseProjectCreationType({
  closeModalAndRemoveData,
  setCreationType,
  setProjectType,
}: ICreationTypeProps) {
  const { lang } = useTranslation();

  const createTelletChoices: IButtons[] = [
    {
      icon: <MessageSquare size={55} />,
      title: lang.get('msg.telletInterview'),
      description: lang.get('msg.telletInterviewDescription'),
      icon_color: 'text-interview',
      bg_color: 'bg-interview-bg',
      type: 'scratch',
      projectType: 'interview',
    },
    {
      icon: <Code size={55} />,
      title: lang.get('msg.telletEmbed'),
      description: lang.get('msg.telletEmbedDescription'),
      icon_color: 'text-embed',
      bg_color: 'bg-embed-bg',
      type: 'scratch',
      projectType: 'embed',
    },
  ];

  const handleSelect = (button: IButtons) => {
    setProjectType(button.projectType);
    setCreationType(button.type);
  };

  return (
    <div className="relative flex flex-col items-center justify-center w-full h-full gap-10 overflow-hidden overflow-y-auto text-center">
      {/* close modal */}
      <button className="absolute top-2 right-2" type="button" onClick={closeModalAndRemoveData}>
        <X size={24} />
      </button>
      {/* modal content */}
      <div className="flex flex-col items-center justify-center gap-2">
        <h3 className="text-xl font-bold">{lang.get('msg.createNewTellet')}</h3>
        <p className="mt-4">{lang.get('msg.chooseProjectType')}</p>
      </div>
      {/* pick creation choice */}
      <div className="flex flex-wrap justify-center gap-5">
        {React.Children.toArray(
          createTelletChoices.map((button) => (
            <button
              onClick={() => handleSelect(button)}
              className={cn(
                'w-full max-w-[260px] h-[260px] border shadow border-dark-text/30 hover:border-dark-text/50',
                'rounded-md hover:shadow-md transition-all ease-in-out hover:scale-[1.02] flex flex-col items-center justify-center text-center',
              )}
              type="button"
            >
              <div
                className={`p-1 ${button.bg_color} ${button.icon_color} mb-7 rounded-xl flex items-center justify-center w-[65px] h-[65px]`}
              >
                {button.icon}
              </div>
              <p className="mb-3 text-lg font-semibold">{button.title}</p>
              <p className="px-8 text-sm text-light-gray">{button.description}</p>
            </button>
          )),
        )}
      </div>
    </div>
  );
}

export default ChooseProjectCreationType;
